import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Check, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import apiClient from '../utils/apiClient';
import { toast } from 'sonner';
import { trackPricingView, trackCheckoutStart } from '../utils/analytics';
import { APP_NAME } from '../config/branding';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import PRICING_FAQS from '../data/pricing-faqs.json';

const Pricing = () => {
  const [plans, setPlans] = useState([]);
  const [billingCycle, setBillingCycle] = useState('annual');
  const [annualLabel, setAnnualLabel] = useState('2 months included');
  const [loading, setLoading] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activePromo, setActivePromo] = useState('');
  const [foundingStatus, setFoundingStatus] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    trackPricingView();
    fetchPricing();
    const token = localStorage.getItem('token');
    setIsAuthenticated(!!token);
    // Founding promo carry-through (?promo=FOUNDING40)
    const params = new URLSearchParams(location.search);
    const promoParam = params.get('promo');
    if (promoParam) {
      sessionStorage.setItem('founding_promo', promoParam.toUpperCase());
      setActivePromo(promoParam.toUpperCase());
    } else if (sessionStorage.getItem('founding_promo')) {
      setActivePromo(sessionStorage.getItem('founding_promo'));
    }
    if (location.state?.autoCheckout && token) {
      const { planType, billingCycle: intendedCycle } = location.state;
      if (planType && intendedCycle && planType !== 'free') {
        setBillingCycle(intendedCycle);
        toast.success('Account created. Select a plan below to continue.');
      }
    }
  }, [location.state]);

  const fetchPricing = async () => {
    try {
      const res = await apiClient.get('/pricing');
      setPlans(res.data.plans || []);
      setBillingCycle(res.data.billing_default || 'annual');
      setAnnualLabel(res.data.annual_label || '2 months included');
    } catch (err) {
      console.error('Failed to load pricing:', err);
    } finally {
      setPageLoading(false);
    }
    try {
      const s = await apiClient.get('/promotions/founding40-status', { skipErrorToast: true });
      setFoundingStatus(s.data);
    } catch {
      // graceful fallback: banner shows original copy
    }
  };

  const handleUpgrade = async (plan) => {
    const planType = plan.plan_key;
    if (plan.hide_price || plan.cta_type === 'contact_sales') {
      navigate(`/contact-sales?plan_key=${plan.plan_key}`);
      return;
    }
    if (!isAuthenticated) {
      if (planType === 'free') { navigate(activePromo ? `/register?promo=${encodeURIComponent(activePromo)}` : '/register'); return; }
      localStorage.setItem('intended_plan', JSON.stringify({ planKey: plan.plan_key, billingCycle }));
      toast.info('Please create an account to continue.');
      navigate(activePromo ? `/register?promo=${encodeURIComponent(activePromo)}` : '/register');
      return;
    }
    if (planType === 'free') return;
    trackCheckoutStart();
    // Route to checkout page — no Stripe IDs exposed to frontend
    const promoSuffix = activePromo ? `&promo=${encodeURIComponent(activePromo)}` : '';
    navigate(`/checkout?plan_key=${plan.plan_key}&interval=${billingCycle}${promoSuffix}`);
  };

  const handleClaimFounding = () => {
    sessionStorage.setItem('founding_promo', 'FOUNDING40');
    setActivePromo('FOUNDING40');
    if (!isAuthenticated) {
      navigate('/register?promo=FOUNDING40');
    } else {
      toast.success('Code FOUNDING40 will be pre-filled at checkout. Pick a plan below.');
    }
  };

  const PriceDisplay = ({ plan }) => {
    if (plan.hide_price) {
      return (
        <div className="pt-1">
          <span className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
            {plan.price_display_text || 'Custom Pricing'}
          </span>
          <p className="text-xs text-muted-foreground mt-1.5">Pricing scales with team size and usage requirements.</p>
        </div>
      );
    }

    const isAnnual = billingCycle === 'annual';
    const price = isAnnual ? plan.price_annual : plan.price_monthly;
    const compareAt = isAnnual ? plan.compare_at_annual : plan.compare_at_monthly;
    const showCompare = plan.show_compare_at && compareAt != null && compareAt > price;

    if (price === 0) {
      return (
        <div className="pt-1">
          <span className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground">$0</span>
          <span className="text-sm text-muted-foreground ml-1.5">no commitment</span>
        </div>
      );
    }

    return (
      <div className="space-y-1.5 pt-1">
        <div>
          {showCompare && (
            <span className="text-base text-muted-foreground line-through mr-2">${compareAt}</span>
          )}
          <span className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground">${price}</span>
          <span className="text-sm text-muted-foreground ml-1">/{isAnnual ? 'yr' : 'mo'}</span>
        </div>
        {isAnnual && (
          <p className="text-xs text-muted-foreground">${Math.round(price / 12)}/mo, billed annually</p>
        )}
        {showCompare && plan.has_active_promo && (
          <p className="text-xs text-muted-foreground">
            Introductory rate applied
          </p>
        )}
      </div>
    );
  };

  if (pageLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground/30" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Seo
        title="CBAIQ Pricing | Labour Relations AI for Managers and HR"
        description="CBAIQ pricing by agreements and seats: Starter $299/mo, Pro $799/mo with risk-scored case review, Organization plans with SSO and audit exports. Start with a guided 14-day pilot — no credit card."
        path="/pricing"
        jsonLd={[
          ...(plans.length > 0 ? [{
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "CBAIQ",
            "url": "https://cbaiq.com",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web",
            "description": "AI-powered collective agreement interpretation software for labour relations teams.",
            "offers": plans.filter((p) => !p.hide_price).map((p) => ({
              "@type": "Offer",
              "name": `CBAIQ ${p.display_name}`,
              "price": String(p.price_monthly ?? 0),
              "priceCurrency": "USD",
              "url": "https://cbaiq.com/pricing",
              "description": p.description || undefined,
            })),
          }] : []),
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": PRICING_FAQS.map(({ q, a }) => ({
              "@type": "Question",
              "name": q,
              "acceptedAnswer": { "@type": "Answer", "text": a },
            })),
          },
        ]}
      />
      {/* Header */}
      <header className="border-b border-border/40 sticky top-0 z-40 bg-background/95 backdrop-blur-sm" data-testid="pricing-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-heading font-semibold text-lg tracking-tight">{APP_NAME}</span>
            </Link>
            <div className="flex items-center gap-3">
              {isAuthenticated ? (
                <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} data-testid="go-to-dashboard-btn">
                  Dashboard
                </Button>
              ) : (
                <>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/login')} data-testid="sign-in-btn">Sign In</Button>
                  <Button size="sm" onClick={() => navigate('/register')} data-testid="get-started-btn">Get Started</Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Back */}
          <div className="pt-8 pb-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-1.5 text-muted-foreground hover:text-foreground" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" /> Back
            </Button>
          </div>

          {/* Hero */}
          <div className="text-center pt-4 pb-10 max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-foreground leading-tight mb-5" data-testid="pricing-heading">
              Agreement Intelligence for<br className="hidden sm:block" /> Negotiation Environments
            </h1>
            <p className="text-base text-muted-foreground leading-relaxed max-w-xl mx-auto">
              Priced by agreements and seats — so your whole team, including front-line supervisors, can use it.
            </p>
          </div>

          {/* Value anchor */}
          <div className="max-w-3xl mx-auto mb-10 border border-border rounded-lg bg-muted/20 px-6 py-5 text-center" data-testid="value-anchor-strip">
            <p className="text-sm text-foreground leading-relaxed">
              One arbitration costs <span className="font-semibold">$20,000–$75,000</span>. One question to
              outside counsel costs <span className="font-semibold">$400</span>. CBAIQ starts at{" "}
              <span className="font-semibold">$299/month</span>.
            </p>
          </div>

          {/* Billing Toggle */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center border border-border rounded-lg p-1 bg-muted/30" data-testid="billing-toggle">
              <button
                onClick={() => setBillingCycle('annual')}
                data-testid="billing-annual"
                className={`px-5 py-2 rounded-md text-sm font-medium transition-all ${
                  billingCycle === 'annual'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Annual
                <span className="ml-2 text-xs text-muted-foreground font-normal">({annualLabel})</span>
              </button>
              <button
                onClick={() => setBillingCycle('monthly')}
                data-testid="billing-monthly"
                className={`px-5 py-2 rounded-md text-sm font-medium transition-all ${
                  billingCycle === 'monthly'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Monthly
              </button>
            </div>
          </div>

          {/* Founding customer banner */}
          <div className="max-w-xl mx-auto mb-12 text-center" data-testid="founding-customer-banner">
            <p className="text-sm text-foreground font-medium">
              Founding customer program: 40% off your first 12 months in exchange for feedback and a testimonial.
            </p>
            <p className="text-xs text-muted-foreground mt-1" data-testid="founding-seats-remaining">
              {foundingStatus && foundingStatus.total_slots
                ? (foundingStatus.available
                    ? (foundingStatus.slots_remaining <= 10
                        ? `Only ${foundingStatus.slots_remaining} of ${foundingStatus.total_slots} founding seats left.`
                        : `${foundingStatus.slots_remaining} of ${foundingStatus.total_slots} founding seats remaining.`)
                    : 'The founding program is full — standard pricing applies.')
                : 'Limited to the first 25 organizations.'}
            </p>
            {(!foundingStatus || foundingStatus.available) && (
              <button
                type="button"
                onClick={handleClaimFounding}
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-foreground underline underline-offset-4 hover:text-foreground/70 transition-colors"
                data-testid="founding-claim-btn"
              >
                Claim founding pricing with code FOUNDING40 →
              </button>
            )}
            {activePromo === 'FOUNDING40' && (!foundingStatus || foundingStatus.available) && (
              <p className="text-xs text-emerald-700 mt-2" data-testid="founding-promo-active-note">
                Code FOUNDING40 will be applied at checkout.
              </p>
            )}
          </div>

          {/* Plan Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12 max-w-5xl mx-auto" data-testid="pricing-plans">
            {plans.map((plan) => (
              <Card
                key={plan.plan_key}
                data-testid={`plan-card-${plan.plan_key}`}
                className={`relative flex flex-col transition-shadow ${
                  plan.highlight_plan
                    ? 'border-foreground/20 shadow-md'
                    : 'border-border'
                }`}
              >
                {plan.highlight_plan && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="border border-foreground/30 text-foreground px-3 py-1 rounded-full text-xs font-medium tracking-wide bg-background" data-testid="highlight-badge">
                      Most Popular
                    </span>
                  </div>
                )}

                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold">{plan.display_name}</CardTitle>
                  <p className="text-xs text-muted-foreground">{plan.description}</p>
                  <div className="mt-3">
                    <PriceDisplay plan={plan} />
                  </div>
                </CardHeader>

                <CardContent className="flex-1 pt-2">
                  <ul className="space-y-2">
                    {(plan.feature_list || []).map((feature, idx) => {
                      const isCrossSell = feature.startsWith('Need multi-user');
                      if (isCrossSell) {
                        return (
                          <li key={idx} className="pt-2 mt-2 border-t border-border">
                            <span className="text-xs text-muted-foreground italic">{feature}</span>
                          </li>
                        );
                      }
                      return (
                        <li key={idx} className="flex items-start gap-2">
                          <Check className="w-3.5 h-3.5 text-foreground/70 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-foreground/80 leading-snug">{feature}</span>
                        </li>
                      );
                    })}
                    {(plan.limitation_list || []).map((lim, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-center text-muted-foreground leading-none">&mdash;</span>
                        <span className="text-sm text-muted-foreground leading-snug">{lim}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>

                <CardFooter className="pt-4">
                  <Button
                    onClick={() => handleUpgrade(plan)}
                    disabled={loading === plan.plan_key}
                    className="w-full"
                    variant={plan.highlight_plan ? 'default' : 'outline'}
                    data-testid={`plan-cta-${plan.plan_key}`}
                  >
                    {loading === plan.plan_key ? 'Processing...' : (plan.hide_price ? 'Contact Sales' : plan.cta_label)}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          {/* Guided pilot strip (replaces free plan) */}
          <div className="max-w-3xl mx-auto mb-20 border border-foreground/20 rounded-lg px-7 py-8 text-center" data-testid="guided-pilot-strip">
            <h2 className="text-lg font-semibold text-foreground mb-2">Not ready to commit?</h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6 max-w-lg mx-auto">
              Guided 14-day pilot — upload one agreement, get 20 answered questions, onboarding call included.
              No credit card.
            </p>
            <Button size="lg" onClick={() => navigate('/register')} data-testid="start-guided-pilot-btn">
              Start a guided pilot
            </Button>
          </div>

          {/* Pricing FAQ */}
          <div className="max-w-3xl mx-auto mb-16" data-testid="pricing-faq">
            <h2 className="text-xl font-semibold text-foreground mb-8 text-center">Pricing FAQ</h2>
            <div className="space-y-7">
              {PRICING_FAQS.map(({ q, a }, idx) => (
                <div key={q} data-testid={`pricing-faq-item-${idx}`}>
                  <h3 className="text-sm font-semibold text-foreground mb-1.5">{q}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Footer notes */}
          <div className="text-center pb-16 space-y-3">
            <p className="text-xs text-muted-foreground max-w-md mx-auto" data-testid="usage-footnote">
              Usage is measured based on document size and complexity. Actual questions and case reviews per month may vary.
            </p>
            <p className="text-xs text-muted-foreground">
              All plans include enterprise-grade data privacy standards. Subscriptions may be canceled at any time.
            </p>
          </div>

        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Pricing;
